import { useState } from "react";
import {
  MEDICAL_ESCALATION_STANDARD_REPLY,
  MEDICAL_QUESTION_CATEGORIES,
  MEDICAL_QUESTION_CATEGORY_LABELS,
  type MedicalQuestionCategory,
} from "@care-by-ella/contracts";
import { createHandoff, postMedicalEscalationReply } from "@/lib/handoff-api";
import { useReadOnly } from "./SupportView";

// TES-986 + TES-1120 + TES-1119 — the coach's "Skicka till läkare" on one
// patient message in the coach chat.
//
// The server opens a medical review case with her own words, due in two
// working days (Ö-341). The coach may name the question's category, and may
// send Emelie's standard reply (Ö-333) to the patient as herself — only
// here, after an actual escalation, never on its own. The reply is a second
// call that names the handoff, so the server can refuse it without one.

export type SendToDoctorOutcome = "sent" | "sent_reply_failed";

export function SendToDoctorForm({
  patientId,
  messageId,
  onDone,
  onCancel,
  onError,
}: {
  patientId: string;
  messageId: string;
  onDone: (outcome: SendToDoctorOutcome) => void;
  onCancel: () => void;
  onError: (e: unknown) => void;
}) {
  const [category, setCategory] = useState<MedicalQuestionCategory | "">("");
  const [sendReply, setSendReply] = useState(false);
  const [busy, setBusy] = useState(false);
  // An admin reading the chat in support mode never escalates from it.
  const readOnly = useReadOnly();

  async function submit() {
    if (busy) return;
    setBusy(true);
    try {
      const handoff = await createHandoff(patientId, {
        toRole: "doctor",
        priority: "medium",
        reason: "Skicka till läkare",
        sourceMessageId: messageId,
        ...(category ? { category } : {}),
      });
      if (sendReply) {
        try {
          await postMedicalEscalationReply(patientId, {
            handoffId: handoff.id,
          });
        } catch {
          onDone("sent_reply_failed");
          return;
        }
      }
      onDone("sent");
    } catch (e) {
      onError(e);
    } finally {
      setBusy(false);
    }
  }

  if (readOnly) return null;

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        void submit();
      }}
      aria-label="Skicka till läkare"
      className="mt-xs flex flex-col gap-xs rounded-md border border-warning/40 bg-warning/5 p-sm text-xs text-textPrimary"
    >
      <label className="flex flex-col gap-xs">
        <span>Kategori (valfri)</span>
        <select
          value={category}
          onChange={(e) =>
            setCategory(e.target.value as MedicalQuestionCategory | "")
          }
          className="rounded-md border border-surfaceMuted bg-background p-xs"
        >
          <option value="">Ingen kategori</option>
          {MEDICAL_QUESTION_CATEGORIES.map((c) => (
            <option key={c} value={c}>
              {MEDICAL_QUESTION_CATEGORY_LABELS[c]}
            </option>
          ))}
        </select>
      </label>
      <label className="flex items-start gap-xs">
        <input
          type="checkbox"
          checked={sendReply}
          onChange={(e) => setSendReply(e.target.checked)}
          className="mt-[3px]"
        />
        <span>
          Skicka standardsvaret till patienten:
          <span className="mt-xs block whitespace-pre-wrap text-textSecondary">
            {MEDICAL_ESCALATION_STANDARD_REPLY}
          </span>
        </span>
      </label>
      <div className="flex justify-end gap-sm">
        <button
          type="button"
          onClick={onCancel}
          className="rounded-md border border-surfaceMuted px-md py-xs text-textSecondary hover:text-textPrimary"
        >
          Avbryt
        </button>
        <button
          type="submit"
          disabled={busy}
          className="rounded-md bg-brand px-md py-xs text-white hover:opacity-90 disabled:opacity-50"
        >
          {busy ? "Skickar…" : "Skicka till läkare"}
        </button>
      </div>
    </form>
  );
}
